import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, firstValueFrom, Observable, Subject, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { CustomTableSelectComponent } from 'src/app/components/custom-table-select/custom-table-select.component';
import { InventoryEntryTypeEnums } from 'src/app/enum/inventory-entry-enums';
import { InventoryEntryDetailModel } from 'src/app/models/inventory/inventory-entry-detail.model';
import { InventoryEntryModel } from 'src/app/models/inventory/inventory-entry.model';
import { RentalStockModel } from 'src/app/models/rental/rental-stock-response.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { StoreService } from 'src/app/services/archive/store.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { InventoryEntryDetailService } from '../../../../services/inventory/inventory-entry-detail.service';

@Component({
  selector: 'app-register-inventory-entry-detail',
  templateUrl: './register-inventory-entry-detail.component.html',
  styleUrls: ['./register-inventory-entry-detail.component.scss'],
  standalone: false
})
export class RegisterInventoryEntryDetailComponent implements OnChanges, OnDestroy {

  private readonly articleService = inject(ArticleService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly inventoryEntryDetailService = inject(InventoryEntryDetailService);
  private readonly storeService = inject(StoreService);
  private readonly toastHandlerService = inject(ToastHandlerService);

  @ViewChild('articleSelection') articleSelectionModalComponent?: CustomModalComponent | null = null;

  @Input() disabled: boolean = false;
  @Input() inventoryEntryContract: InventoryEntryModel = new InventoryEntryModel();
  @Input() inventoryEntryDetailContract: InventoryEntryDetailModel = new InventoryEntryDetailModel();
  @Output() isFormView = new EventEmitter<boolean>();
  @Output() updateListDetail = new EventEmitter<boolean>();

  articleId: number = 0;
  ascendantArticle: boolean = true;
  columnSortArticle: string | null = null;
  componentTableSelection = CustomTableSelectComponent;
  dataArticle: PagedResponseModel<any> = new PagedResponseModel<any>();
  defaultStoreIdByBranch: number | null = null;
  discountRate: number = 0;
  filtersArticle: ColumnResponseModel[] = []
  formInventoryEntryDetail: FormGroup = new FormGroup({});
  hasDevolutionEntries: boolean = false;
  headerArticle: ColumnResponseModel[] = [];
  inputsForArticlesSelection$ = new BehaviorSubject<any>({
    headers: [],
    data: [],
    showCheckbox: false,
    showRecover: true,
    isMultipleSelect: true,
    filters: [],
    actions: []
  })
  inventoryEntryDetailId: number = 0;
  inventoryEntryDetailOriginId: number | null = null;
  ivaRate: number = 0;
  limitArticle: number = 5;
  listArticles: any[] = [];
  listStores: any[] = [];
  output$ = new Subject<any>();
  pageArticle: number = 1;
  pendingQuantity: number = 0;
  purchaseOrderDetailId: number | null = null;
  quantityPendingFromBackend: number = 0;
  storeId: number = 0;
  subscription: Subscription;
  swRequirePurchaseOrder: boolean = false;
  unitValue: number = 0;

  ngOnInit() {
    this.validateForm(new InventoryEntryDetailModel());
    this.subscription = this.output$.subscribe(({ type, payload }) => {
      switch (type) {
        case 'onPageChange':
          this.onPageChangeArticle(payload);
          break;
        case 'onFilterChange':
          this.onFilterChange(payload);
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
  }

  async ngOnChanges(changes: SimpleChanges) {
    const change = changes["inventoryEntryDetailContract"];
    const inventoryEntry = changes["inventoryEntryContract"];
    if (inventoryEntry && !inventoryEntry.firstChange) {
      const contract = this.inventoryEntryContract;
      this.swRequirePurchaseOrder = contract.swRequirePurchaseOrder;
      this.hasDevolutionEntries = contract.inventoryEntryTypeId === InventoryEntryTypeEnums.devolution;
      await Promise.all([
        this.getListStoresForSelect(contract.branchId, contract.inventoryId),
        this.getListArticlesForSelect()
      ]);
    }

    if (!change || change.firstChange) return;
    const contract = this.inventoryEntryDetailContract;
    this.storeId = 0;
    this.articleId = 0;
    this.pendingQuantity = 0;
    this.validateForm(contract);

    if (contract.inventoryEntryDetailId !== 0) {
      this.inventoryEntryDetailId = contract.inventoryEntryDetailId;
      await Promise.all([
        this.getStore(contract.storeId, false),
        this.getArticle(contract.articleId, false)
      ]);

      if (this.swRequirePurchaseOrder) {
        this.purchaseOrderDetailId = contract.purchaseOrderDetailId;
        this.inventoryEntryDetailOriginId = contract.inventoryEntryDetailOriginId;
        await this.getQuantityPendingByArticle();
      } else {
        await this.getStocksByArticleIdStoreId();
      }
    } else {
      const swDevolutionStore = this.hasDevolutionEntries && this.swRequirePurchaseOrder;
      if (this.defaultStoreIdByBranch != null && !swDevolutionStore) {
        await this.getStore(this.defaultStoreIdByBranch, false);
      }
    }
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  calculateValues(): void {
    const form = this.formInventoryEntryDetail;
    const quantity = form.get("quantity")?.value ?? 0;

    if (this.swRequirePurchaseOrder && !this.validateQuantity(quantity)) return;

    const unitValue = form.get("unitValue")?.value ?? 0;
    const discount = form.get("discount")?.value ?? 0;
    const iva = form.get("iva")?.value ?? 0;

    const unitValueFinal = unitValue * ((100 - discount) / 100) * (1 + iva / 100);
    const totalValue = quantity * unitValueFinal;
    form.patchValue({ totalValue });
  }

  clearForm() {
    this.storeId = 0;
    this.articleId = 0;
    this.pendingQuantity = 0;
    this.quantityPendingFromBackend = 0;
    const modelInventoryEntryDetail = new InventoryEntryDetailModel;
    this.validateForm(modelInventoryEntryDetail)
    this.isFormView.emit(false);
  }

  async getArticle(articleId: number | null, reload: boolean) {
    if (articleId == null && reload) {
      this.storeId = 0;
      this.articleId = 0;
      this.inventoryEntryDetailId = 0;
      this.inventoryEntryDetailOriginId = null;
      this.purchaseOrderDetailId = null;
      this.pendingQuantity = 0;
      this.quantityPendingFromBackend = 0;
      this.unitValue = 0;
      this.discountRate = 0;
      this.ivaRate = 0;
      this.inventoryEntryDetailContract = new InventoryEntryDetailModel();
      this.validateForm(this.inventoryEntryDetailContract);

      const swDevolutionStore = this.hasDevolutionEntries && this.swRequirePurchaseOrder;
      if (this.defaultStoreIdByBranch != null && !swDevolutionStore) {
        await this.getStore(this.defaultStoreIdByBranch, false);
      }
      return;
    }

    let validArticle = null;
    if (articleId != null) {
      const article = await firstValueFrom(this.articleService.getArticleById(articleId));
      if (article) {
        validArticle = articleId;
        if (this.hasDevolutionEntries) {
          this.listArticles = [{ label: `${article.code} - ${article.name}`, value: articleId }];
        }
      }
      if (this.swRequirePurchaseOrder && !this.hasDevolutionEntries) {
        await this.setDataPurchaseOrderArticle(articleId);
      }
    }

    this.articleId = validArticle ?? 0;
    this.formInventoryEntryDetail.controls["articleId"].patchValue(validArticle);
    if (reload) {
      await this.getInventoryEntryDetailByKeys();
    }
  }

  async getInventoryEntryDetailByKeys() {
    if (this.storeId === 0 || this.articleId === 0) return;

    const entryId = this.inventoryEntryContract.inventoryEntryId;
    const response = await firstValueFrom(
      this.inventoryEntryDetailService.getInventoryEntryDetailByKeys(entryId, this.storeId, this.articleId));
    if (response.inventoryEntryDetailId === 0) {
      this.inventoryEntryDetailContract = new InventoryEntryDetailModel();
      this.inventoryEntryDetailId = 0;
      this.inventoryEntryDetailContract.storeId = this.storeId;
      this.inventoryEntryDetailContract.articleId = this.articleId;

      if (this.swRequirePurchaseOrder) {
        this.inventoryEntryDetailContract.purchaseOrderDetailId = this.purchaseOrderDetailId;
        this.inventoryEntryDetailContract.inventoryEntryDetailOriginId = this.inventoryEntryDetailOriginId;
        this.inventoryEntryDetailContract.unitValue = this.unitValue;
        this.inventoryEntryDetailContract.discount = this.discountRate;
        this.inventoryEntryDetailContract.iva = this.ivaRate;
        await this.getQuantityPendingByArticle();
      }
      this.validateForm(this.inventoryEntryDetailContract);
      this.formInventoryEntryDetail.controls["pendingQuantity"].patchValue(this.pendingQuantity);
      await this.getStocksByArticleIdStoreId();
      return;
    }

    this.inventoryEntryDetailContract = response;
    this.inventoryEntryDetailId = response.inventoryEntryDetailId;
    this.validateForm(response);
    if (this.swRequirePurchaseOrder) {
      this.purchaseOrderDetailId = this.inventoryEntryDetailContract.purchaseOrderDetailId;
      this.inventoryEntryDetailOriginId = this.inventoryEntryDetailContract.inventoryEntryDetailOriginId;
      await this.getQuantityPendingByArticle()
    }
    await Promise.all([
      this.getStore(response.storeId, false),
      this.getArticle(response.articleId, false)
    ]);

    await this.getStocksByArticleIdStoreId();
  }

  async getListArticlesForSelect() {
    const inventoryEntryContract = this.inventoryEntryContract;
    try {
      let listArticles: any[] = [];
      if (!this.hasDevolutionEntries) {
        listArticles = await firstValueFrom(
          this.inventoryEntryDetailService.getAllArticlesForEntriesForSelectAsync(
            inventoryEntryContract.providerId, this.swRequirePurchaseOrder, inventoryEntryContract.inventoryEntryId
          )
        );
      } else {
        listArticles = this.listArticles.filter(x => x.value === this.articleId);
      }
      if (inventoryEntryContract === this.inventoryEntryContract) {
        this.listArticles = listArticles;
      }
    } catch (error) {
      console.error('Error al obtener la lista de artículos.', error);
    }
  }

  async getListStoresForSelect(branchId: number, inventoryId: number) {
    try {
      this.listStores = await firstValueFrom(this.storeService.getStoresActivesByBranchInventoryForSelect(branchId, inventoryId));
      this.defaultStoreIdByBranch = await firstValueFrom(this.storeService.getDefaultStoreIdByBranch(branchId));
    } catch (error) {
      console.error('Error al obtener la lista de bodegas.', error);
    }
  }

  async getStocksByArticleIdStoreId() {
    if (this.storeId != 0 && this.articleId != 0) {
      const rentalStockModel = new RentalStockModel();
      rentalStockModel.articleId = this.articleId;
      rentalStockModel.storeId = this.storeId;
      rentalStockModel.dateMovement = this.inventoryEntryContract.dateEntry;

      const stock = await firstValueFrom(this.inventoryEntryDetailService.getStocksByArticleIdStoreId(rentalStockModel));
      this.formInventoryEntryDetail.controls["stock"].patchValue(stock);
    }
  }

  async getStore(storeId: number | null, reload: boolean) {
    let validStore = null;
    if (storeId != null) {
      const store = await firstValueFrom(this.storeService.getStoreById(storeId));
      if (store) {
        validStore = storeId;
      }
    }

    this.storeId = validStore ?? 0;
    this.formInventoryEntryDetail.controls["storeId"].patchValue(validStore);

    if (reload) {
      this.inventoryEntryDetailId = 0;
      const modelInventoryEntryDetail = new InventoryEntryDetailModel;
      modelInventoryEntryDetail.articleId = this.articleId;
      modelInventoryEntryDetail.storeId = this.storeId;
      this.validateForm(modelInventoryEntryDetail);
      await this.getInventoryEntryDetailByKeys();
    }
  }

  async handleSelectedItem(selectedItem: any) {
    const articleId = parseInt(this.hasDevolutionEntries ? selectedItem[0]?.articleId : selectedItem[0]?.id);
    if (this.swRequirePurchaseOrder) {
      await this.setDataPurchaseOrder(selectedItem);
    }

    if (this.hasDevolutionEntries) {
      this.listArticles = [{ label: `${selectedItem[0]?.articleCode} - ${selectedItem[0]?.articleName}`, value: articleId }];
      this.storeId = parseInt(selectedItem[0]?.storeId);
      this.formInventoryEntryDetail.controls["storeId"].patchValue(this.storeId);
    }

    this.articleId = articleId;
    this.formInventoryEntryDetail.controls["articleId"].patchValue(articleId);
    await this.getInventoryEntryDetailByKeys();
  }

  updateInputs() {
    this.inputsForArticlesSelection$.next({
      headers: this.headerArticle,
      data: this.dataArticle,
      showCheckbox: true,
      showRecover: true,
      isMultipleSelect: false,
      limitPaginator: true,
      filters: this.filtersArticle,
      actions: [
        {
          label: 'Imágenes',
          icon: 'pi pi-image',
          action: 'view-image'
        }
      ]
    });
  }

  async getColumnsArticle() {
    try {
      let serviceGetColumn: Observable<any>;
      if (this.hasDevolutionEntries) {
        serviceGetColumn = this.inventoryEntryDetailService.getColumnsForArticlesDevolutionForEntriesAsync();
      } else if (this.swRequirePurchaseOrder) {
        serviceGetColumn = this.inventoryEntryDetailService.getColumnsForArticlesPurchaseOrderForEntriesAsync();
      } else {
        serviceGetColumn = this.inventoryEntryDetailService.getColumnsForArticles();
      }
      this.headerArticle = await firstValueFrom(serviceGetColumn);
    } catch (error) {
      console.error('Error al buscar las columnas para la selección de artículos.', error);
    }
  }

  async getArticlesForEntries() {
    try {
      let serviceGetData: Observable<any>;
      if (this.hasDevolutionEntries) {
        serviceGetData = this.inventoryEntryDetailService.getArticlesDevolutionForEntries(
          this.inventoryEntryContract.inventoryEntryId, this.pageArticle, this.limitArticle,
          this.columnSortArticle, this.ascendantArticle, this.filtersArticle
        );
      } else if (this.swRequirePurchaseOrder) {
        serviceGetData = this.inventoryEntryDetailService.getArticlesPurchaseOrderForEntries(
          this.inventoryEntryContract.providerId, this.inventoryEntryContract.inventoryEntryId, this.pageArticle,
          this.limitArticle, this.columnSortArticle, this.ascendantArticle, this.filtersArticle
        );
      } else {
        serviceGetData = this.inventoryEntryDetailService.getArticlesForEntries(
          this.pageArticle, this.limitArticle, this.columnSortArticle,
          this.ascendantArticle, this.filtersArticle
        );
      }
      this.dataArticle = await firstValueFrom(serviceGetData);
      this.updateInputs();
    } catch (error) {
      console.error('Error al obtener la lista de los artículos', error);
    }
  }

  async getQuantityPendingByArticle() {
    if (this.hasDevolutionEntries) {
      await this.getQuantityPendingDevolution();
      return;
    }
    if (!this.purchaseOrderDetailId || !this.articleId) return;

    try {
      this.quantityPendingFromBackend = await firstValueFrom(
        this.inventoryEntryDetailService.getQuantityPendingPuchaseOrderByArticle(this.purchaseOrderDetailId, this.articleId)
      );
      this.pendingQuantity = this.quantityPendingFromBackend;
      this.formInventoryEntryDetail.controls["pendingQuantity"].patchValue(this.quantityPendingFromBackend);
    } catch (error) {
      console.error("Error al obtener la cantidad pendiente de la orden de compra por artículo", error);
    }
  }

  async getQuantityPendingDevolution() {
    if (!this.inventoryEntryDetailOriginId) return;

    try {
      this.quantityPendingFromBackend = await firstValueFrom(
        this.inventoryEntryDetailService.getQuantityPendingDevolutionByOrigin(this.inventoryEntryDetailOriginId)
      );
      this.pendingQuantity = this.quantityPendingFromBackend;
      this.formInventoryEntryDetail.controls["pendingQuantity"].patchValue(this.quantityPendingFromBackend);
    } catch (error) {
      console.error("Error al obtener la cantidad pendiente de devolución", error);
    }
  }

  async onPageChangeArticle(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.pageArticle = event.page;
    this.limitArticle = event.pageSize;
    this.columnSortArticle = event.sortField || null;
    this.ascendantArticle = event.sortOrder === 1;
    await this.getArticlesForEntries();
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filtersArticle = this.filtersArticle.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filtersArticle.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);
    this.filtersArticle = this.removeDuplicates(filters);
    this.filtersArticle.forEach((filter: any) => this.processFilter(filter));
    this.updateInputs();
  }

  async openArticleSelectionModal() {
    if (this.hasDevolutionEntries && !this.swRequirePurchaseOrder) {
      this.toastHandlerService.showToast(
        'warning',
        'Las entradas de tipo Devolución requieren orden de compra. Active "Req.Ord. Compra" en el encabezado y guarde la entrada.'
      );
      return;
    }

    this.filtersArticle = [];
    await Promise.all([
      this.getColumnsArticle(),
      this.getArticlesForEntries()]
    );
    this.updateInputs();
    this.articleSelectionModalComponent && this.articleSelectionModalComponent.show();
  }

  async send() {
    if (this.formInventoryEntryDetail.valid) {
      //Valido cantidades antes de asignar datos al modelo
      if (this.swRequirePurchaseOrder && !this.validateQuantity(this.formInventoryEntryDetail.get("quantity")?.value)) return;

      const modelInventoryEntryDetail = this.formInventoryEntryDetail.getRawValue();
      if (modelInventoryEntryDetail.totalValue < 0) {
        this.toastHandlerService.showToast(
          'warning',
          'El valor total no puede ser negativo.',
        );
        return;
      }

      try {
        if (this.inventoryEntryDetailId == 0) {
          modelInventoryEntryDetail.inventoryEntryId = this.inventoryEntryContract.inventoryEntryId;
          await firstValueFrom(
            this.inventoryEntryDetailService.createInventoryEntryDetail(modelInventoryEntryDetail)
          );
          this.toastHandlerService.showToast(
            'success',
            'El detalle fue creado exitosamente.'
          );
        }
        else {
          this.inventoryEntryDetailContract.quantity = modelInventoryEntryDetail.quantity;
          this.inventoryEntryDetailContract.unitValue = modelInventoryEntryDetail.unitValue;
          this.inventoryEntryDetailContract.discount = modelInventoryEntryDetail.discount;
          this.inventoryEntryDetailContract.iva = modelInventoryEntryDetail.iva;
          this.inventoryEntryDetailContract.totalValue = modelInventoryEntryDetail.totalValue;
          await firstValueFrom(
            this.inventoryEntryDetailService.updateInventoryEntryDetail(this.inventoryEntryDetailContract)
          );
          this.toastHandlerService.showToast(
            'success',
            'El detalle fue modficado exitosamente.'
          );
        }

        this.inventoryEntryDetailContract = new InventoryEntryDetailModel();
        this.clearForm();
        this.isFormView.emit(true);
        this.updateListDetail.emit(true);
      } catch (error) {
        console.error('Error al guardar el detalle.', error);
      }
    } else {
      this.showValidationMessages();
    }
  }

  async setDataPurchaseOrderArticle(articleId: number) {
    if (articleId !== 0) {
      const article = this.listArticles.find(x => x.value === articleId);
      if (article) {
        this.purchaseOrderDetailId = parseInt(article.purchaseOrderDetailId ?? 0);
        this.pendingQuantity = parseFloat(article.pendingQuantity ?? 0);
        this.unitValue = parseFloat(article.unitValue ?? 0);
        this.discountRate = parseFloat(article.discountRate ?? 0);
        this.ivaRate = parseFloat(article.ivaRate ?? 0);
      }
    }
  }

  async setDataPurchaseOrder(selectedItem: any) {
    this.purchaseOrderDetailId = parseInt(selectedItem[0]?.purchaseOrderDetailId);
    this.inventoryEntryDetailOriginId = this.hasDevolutionEntries ? parseInt(selectedItem[0]?.id) : null;
    this.pendingQuantity = parseFloat(selectedItem[0]?.pendingQuantity);
    this.unitValue = parseFloat(selectedItem[0]?.unitValue);
    this.discountRate = parseFloat(selectedItem[0]?.discountRate);
    this.ivaRate = parseFloat(selectedItem[0]?.ivaRate);
  }

  showValidationMessages(): void {
    const controlMessages: Record<string, Record<string, string>> = {
      storeId: {
        required: 'El campo bodega es obligatorio.'
      },
      articleId: {
        required: 'El campo artículo es obligatorio.'
      },
      quantity: {
        required: 'El campo cantidad es obligatorio.',
        min: 'La cantidad mínima es 1.'
      },
      unitValue: {
        min: 'El valor unitario mínimo es 0.',
        required: 'El campo valor unitario es obligatorio.'
      },
      discount: {
        required: 'El campo descuento es obligatorio.',
        min: 'El descuento mínimo es 0.',
        max: 'El descuento máximo es 100.'
      },
      iva: {
        required: 'El campo % IVA es obligatorio.',
        min: 'El % IVA mínimo es 0.',
        max: 'El % IVA máximo es 100.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formInventoryEntryDetail.get(controlName);

      if (control && control.invalid && control.errors) {
        const errors = control.errors;

        Object.keys(errors).forEach(errorKey => {
          const message = controlMessages[controlName][errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  validateForm(inventoryEntryDetail: InventoryEntryDetailModel) {
    this.formInventoryEntryDetail = this.formBuilder.group({
      storeId: [inventoryEntryDetail.storeId, [Validators.required, Validators.min(1)]],
      articleId: [inventoryEntryDetail.articleId, [Validators.required, Validators.min(1)]],
      stock: [{ value: 0, disabled: true }],
      pendingQuantity: [{ value: 0, disabled: true }, [Validators.min(0)]],
      quantity: [inventoryEntryDetail.quantity, [Validators.required, Validators.min(1)]],
      unitValue: [inventoryEntryDetail.unitValue, [Validators.required, Validators.min(0)]],
      discount: [inventoryEntryDetail.discount, [Validators.required, Validators.min(0), Validators.max(100)]],
      iva: [inventoryEntryDetail.iva, [Validators.required, Validators.min(0), Validators.max(100)]],
      totalValue: [{ value: inventoryEntryDetail.totalValue, disabled: true }],
      purchaseOrderDetailId: [inventoryEntryDetail.purchaseOrderDetailId],
      inventoryEntryDetailOriginId: [inventoryEntryDetail.inventoryEntryDetailOriginId]
    });

    if (inventoryEntryDetail.inventoryEntryDetailId !== 0) this.calculateValues();

    setTimeout(() => {
      if (this.swRequirePurchaseOrder) {
        this.formInventoryEntryDetail.controls["unitValue"].disable(),
          this.formInventoryEntryDetail.controls["discount"].disable(),
          this.formInventoryEntryDetail.controls["iva"].disable()
      }

      if (this.hasDevolutionEntries && this.swRequirePurchaseOrder) {
        this.formInventoryEntryDetail.controls["storeId"].disable();
      }
    });
  }

  validateQuantity(newQuantity: number): boolean {
    let previousQuantity = 0;
    if (this.inventoryEntryDetailContract?.inventoryEntryDetailId !== 0) {
      previousQuantity = this.inventoryEntryDetailContract?.quantity ?? 0;
    }

    const pendingGlobal = this.quantityPendingFromBackend ?? 0;
    const differenceQuantity = newQuantity - previousQuantity;

    const newPending = pendingGlobal - differenceQuantity;
    this.pendingQuantity = newPending;
    this.formInventoryEntryDetail.controls["pendingQuantity"].patchValue(newPending);

    if (differenceQuantity > pendingGlobal) {
      let message: string;
      if (this.hasDevolutionEntries) {
        message = `La cantidad a devolver no puede superar la cantidad disponible de la entrada (${pendingGlobal}) "Q. Disp."`;
      } else {
        message = `La cantidad ingresada no puede superar la cantidad pendiente (${pendingGlobal}) "Q. Pte."`;
      }
      this.toastHandlerService.showToast('warning', message);
      return false;
    }
    return true;
  }
}
